import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import DateRangeSelector from './components/DateRangeSelector';
import MetricsDrillDown from './components/MetricsDrillDown';
import SegmentFilter from './components/SegmentFilter';
import CustomReportBuilder from './components/CustomReportBuilder';
import AnalyticsCharts from './components/AnalyticsCharts';
import advancedAnalyticsService from '../../services/advancedAnalyticsService';
import { subDays, startOfDay, endOfDay } from 'date-fns';
import { TrendingUp, Users, Activity, BarChart3, Download, Save } from 'lucide-react';

export default function AdvancedAnalyticsDashboard() {
  const { user, userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [drillDownData, setDrillDownData] = useState(null);
  const [availableSegments, setAvailableSegments] = useState(null);
  const [error, setError] = useState(null);

  // Date range state
  const [dateRange, setDateRange] = useState({
    startDate: startOfDay(subDays(new Date(), 30)),
    endDate: endOfDay(new Date())
  });
  const [comparisonRange, setComparisonRange] = useState(null);
  const [compareMode, setCompareMode] = useState(false);

  // Segment filters state
  const [activeSegments, setActiveSegments] = useState([]);

  // Report builder state
  const [showReportBuilder, setShowReportBuilder] = useState(false);
  const [savedReports, setSavedReports] = useState([]);

  // Extract artist brand colors — only applied when the logged-in user is an artist
  const isArtist = userProfile?.role === 'artist';
  const highlightColor = isArtist
    ? (userProfile?.highlight_color || userProfile?.highlightColor || null)
    : null;
  const secondaryColor = isArtist
    ? (userProfile?.secondary_color || userProfile?.secondaryColor || highlightColor || null)
    : null;

  useEffect(() => {
    if (user) {
      loadInitialData();
    }
  }, [user]);

  useEffect(() => {
    if (dateRange) {
      loadAnalyticsData();
    }
  }, [dateRange, comparisonRange, activeSegments]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [segments, reports] = await Promise?.all([
        advancedAnalyticsService?.getAvailableSegments(),
        advancedAnalyticsService?.getSavedReports()
      ]);
      setAvailableSegments(segments);
      setSavedReports(reports);
    } catch (err) {
      console.error('Error loading initial data:', err);
      setError('Failed to load analytics configuration');
    } finally {
      setLoading(false);
    }
  };

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        startDate: dateRange?.startDate,
        endDate: dateRange?.endDate,
        segments: activeSegments
      };

      if (compareMode && comparisonRange) {
        params.compareStartDate = comparisonRange?.startDate;
        params.compareEndDate = comparisonRange?.endDate;
      }

      const data = await advancedAnalyticsService?.getAnalyticsSummary(params);
      setAnalyticsData(data);
    } catch (err) {
      console.error('Error loading analytics data:', err);
      setError('Failed to load analytics data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleMetricDrillDown = async (metricType) => {
    try {
      setSelectedMetric(metricType);
      setLoading(true);
      
      const data = await advancedAnalyticsService?.getDrillDownData(
        metricType,
        dateRange?.startDate,
        dateRange?.endDate,
        activeSegments
      );
      
      setDrillDownData(data);
    } catch (err) {
      console.error('Error loading drill-down data:', err);
      setError(`Failed to load ${metricType} details`);
    } finally {
      setLoading(false);
    }
  };

  const handleSegmentToggle = (segment) => {
    setActiveSegments(prev => {
      const exists = prev?.find(s => s?.type === segment?.type && s?.value === segment?.value);
      if (exists) {
        return prev?.filter(s => !(s?.type === segment?.type && s?.value === segment?.value));
      }
      return [...prev, segment];
    });
  };

  const handleExportData = async (format) => {
    try {
      const exportData = drillDownData || analyticsData?.primary;
      const content = await advancedAnalyticsService?.exportData(format, exportData);
      
      // Create download link
      const blob = new Blob([content], { 
        type: format === 'csv' ? 'text/csv' : 'application/json' 
      });
      const url = URL?.createObjectURL(blob);
      const link = document?.createElement('a');
      link.href = url;
      link.download = `analytics-export-${new Date()?.toISOString()}.${format}`;
      document?.body?.appendChild(link);
      link?.click();
      document?.body?.removeChild(link);
      URL?.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting data:', err);
      setError('Failed to export data');
    }
  };

  const handleSaveReport = async (reportConfig) => {
    try {
      await advancedAnalyticsService?.saveReportTemplate(reportConfig);
      const updatedReports = await advancedAnalyticsService?.getSavedReports();
      setSavedReports(updatedReports);
      setShowReportBuilder(false);
    } catch (err) {
      console.error('Error saving report:', err);
      setError('Failed to save report template');
    }
  };

  const renderMetricCard = (title, value, change, icon, metricType) => {
    const isPositive = change >= 0;
    return (
      <div 
        className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 cursor-pointer hover:shadow-md transition-shadow"
        onClick={() => handleMetricDrillDown(metricType)}
      >
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">{title}</p>
            <p className="text-3xl font-bold text-gray-900 mt-2">{value?.toLocaleString()}</p>
            {compareMode && change !== null && (
              <div className={`flex items-center mt-2 text-sm ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                <TrendingUp size={16} className={isPositive ? '' : 'rotate-180'} />
                <span className="ml-1">{Math?.abs(change)?.toFixed(1)}%</span>
              </div>
            )}
          </div>
          <div
            className="p-3 rounded-lg"
            style={highlightColor ? { backgroundColor: `${highlightColor}20` } : { backgroundColor: '#EFF6FF' }}
          >
            {icon}
          </div>
        </div>
      </div>
    );
  };

  if (loading && !analyticsData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Artist brand color header accent strip */}
      {highlightColor && (
        <div
          className="h-1 w-full"
          style={{ background: `linear-gradient(to right, ${highlightColor}, ${secondaryColor || highlightColor}, transparent)` }}
        />
      )}

      <div className="flex">
        <Sidebar onToggleCollapse={() => {}} />
        <main className="flex-1 p-8 ml-64">
          <div className="max-w-7xl mx-auto">
            {/* Page Header */}
            <div className="mb-8">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center space-x-3 mb-1">
                    {highlightColor && (
                      <div
                        className="w-1 h-8 rounded-full"
                        style={{ background: `linear-gradient(to bottom, ${highlightColor}, ${secondaryColor || highlightColor})` }}
                      />
                    )}
                    <h1 className="text-3xl font-bold text-gray-900">Advanced Analytics Dashboard</h1>
                  </div>
                  <p className="text-gray-600 mt-2">Comprehensive drill-down analytics with date range comparisons and custom reporting</p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowReportBuilder(!showReportBuilder)}
                    className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <Save size={20} />
                    <span>Custom Report</span>
                  </button>
                  <button
                    onClick={() => handleExportData('csv')}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-white"
                    style={highlightColor ? {
                      background: `linear-gradient(135deg, ${highlightColor}E6, ${secondaryColor || highlightColor}CC)`,
                      boxShadow: `0 2px 8px ${highlightColor}40`
                    } : { backgroundColor: '#2563EB' }}
                  >
                    <Download size={20} />
                    <span>Export Data</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Error Display */}
            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800">{error}</p>
              </div>
            )}

            {/* Date Range Selector with Comparison */}
            <DateRangeSelector
              dateRange={dateRange}
              comparisonRange={comparisonRange}
              compareMode={compareMode}
              onDateRangeChange={setDateRange}
              onComparisonRangeChange={setComparisonRange}
              onCompareModeToggle={setCompareMode}
            />

            {/* Segment Filters */}
            {availableSegments && (
              <SegmentFilter
                availableSegments={availableSegments}
                activeSegments={activeSegments}
                onSegmentToggle={handleSegmentToggle}
              />
            )}

            {/* Key Metrics Grid */}
            {analyticsData?.primary && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {renderMetricCard(
                  'Total Users',
                  analyticsData?.primary?.totalUsers,
                  analyticsData?.changes?.totalUsers,
                  <Users size={24} className="text-blue-600" />,
                  'total_users'
                )}
                {renderMetricCard(
                  'Profile Views',
                  analyticsData?.primary?.profileViews,
                  analyticsData?.changes?.profileViews,
                  <Activity size={24} className="text-green-600" />,
                  'profile_views'
                )}
                {renderMetricCard(
                  'App Downloads',
                  analyticsData?.primary?.appDownloads,
                  analyticsData?.changes?.appDownloads,
                  <Download size={24} className="text-purple-600" />,
                  'app_downloads'
                )}
                {renderMetricCard(
                  'Page Clicks',
                  analyticsData?.primary?.pageClicks,
                  analyticsData?.changes?.pageClicks,
                  <BarChart3 size={24} className="text-orange-600" />,
                  'page_clicks'
                )}
              </div>
            )}

            {/* Interactive Charts */}
            {analyticsData?.primary && (
              <AnalyticsCharts
                primaryData={analyticsData?.primary}
                comparisonData={analyticsData?.comparison}
                compareMode={compareMode}
              />
            )}

            {/* Drill-Down Data Display */}
            {selectedMetric && drillDownData && (
              <MetricsDrillDown
                metricType={selectedMetric}
                data={drillDownData}
                onClose={() => {
                  setSelectedMetric(null);
                  setDrillDownData(null);
                }}
              />
            )}

            {/* Custom Report Builder Modal */}
            {showReportBuilder && (
              <CustomReportBuilder
                availableMetrics={['profile_views', 'content_uploads', 'app_downloads', 'page_clicks', 'active_users']}
                savedReports={savedReports}
                onSave={handleSaveReport}
                onClose={() => setShowReportBuilder(false)}
              />
            )}
          </div>
        </main>
      </div>
    </div>
  );
}